import { useRef, useEffect, useState, useCallback } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { AccountManager } from '../connection/AccountManager';
import { PlanningModeBanner, AccountSelector } from './PlanningModeBanner';
import { useChatStore } from '@/stores/chatStore';
import { useConnectionStore } from '@/stores/connectionStore';

export function ChatContainer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const {
    messages,
    currentConversationId,
    isStreaming,
    streamingContent,
    streamingArtifacts,
    profilingProgress,
    thinkingStatus,
    sendMessage,
    planningMode,
    planningSession,
    planningNeedsAccountSelection,
    planningAvailableAccounts,
    selectPlanningAccount,
    cancelPlanningSession,
  } = useChatStore();
  const { isConnected, isLoading: isConnectionLoading, checkConnection, connectWithDefaults, disconnect, getZoomConnectUrl } = useConnectionStore();

  // Auto-connect on mount
  useEffect(() => {
    const autoConnect = async () => {
      await checkConnection();
      // If not connected after checking, try to connect with defaults
      const store = useConnectionStore.getState();
      if (!store.isConnected) {
        await connectWithDefaults();
      }
    };
    autoConnect();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const handleSendMessage = async (content: string) => {
    await sendMessage(content);
  };

  const handleDisconnect = useCallback(async () => {
    if (confirm('Are you sure you want to disconnect this Zoom account?')) {
      await disconnect();
    }
  }, [disconnect]);

  const handleConnectZoom = useCallback(async () => {
    const url = await getZoomConnectUrl('/chat', 'reader');
    if (url) {
      window.location.href = url;
    }
  }, [getZoomConnectUrl]);

  const handleAction = useCallback((action: string) => {
    switch (action) {
      case 'connect-zoom':
      case 'add-account':
        handleConnectZoom();
        break;
      case 'disconnect-zoom':
        handleDisconnect();
        break;
      case 'manage-accounts':
        setShowAccountManager(true);
        break;
    }
  }, [handleConnectZoom, handleDisconnect]);

  // Show welcome screen if no conversation
  if (!currentConversationId && messages.length === 0) {
    return (
      <>
        <div className="flex h-full flex-col items-center justify-center p-4">
          <h1 className="text-2xl font-medium text-foreground mb-8">What can I help with?</h1>
          <div className="w-full max-w-2xl">
            <MessageInput
              onSend={handleSendMessage}
              onAction={handleAction}
              disabled={isStreaming}
              isConnected={isConnected}
              isConnecting={isConnectionLoading}
            />
          </div>
        </div>
        {showAccountManager && (
          <AccountManager
            onClose={() => setShowAccountManager(false)}
            onAddAccount={handleConnectZoom}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col bg-background">
        {/* Planning mode banner */}
        {planningMode && planningSession && (
          <div className="mx-auto max-w-3xl w-full px-4 pt-4">
            <PlanningModeBanner
              session={planningSession}
              onCancel={cancelPlanningSession}
            />
          </div>
        )}

        {/* Account selector for planning mode */}
        {planningNeedsAccountSelection && planningAvailableAccounts.length > 0 && (
          <div className="mx-auto max-w-3xl w-full px-4">
            <AccountSelector
              accounts={planningAvailableAccounts}
              onSelect={selectPlanningAccount}
            />
          </div>
        )}

        {/* Message list */}
        <div ref={containerRef} className="flex-1 overflow-y-auto">
          <MessageList
            messages={messages}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
            streamingArtifacts={streamingArtifacts}
            profilingProgress={profilingProgress}
            thinkingStatus={thinkingStatus}
          />
        </div>

        {/* Input area */}
        <div className="pb-[10vh]">
          <div className="mx-auto max-w-3xl px-4 py-6">
            <MessageInput
              onSend={handleSendMessage}
              onAction={handleAction}
              disabled={isStreaming}
              isConnected={isConnected}
              isConnecting={isConnectionLoading}
            />
          </div>
        </div>
      </div>
      {showAccountManager && (
        <AccountManager
          onClose={() => setShowAccountManager(false)}
          onAddAccount={handleConnectZoom}
        />
      )}
    </>
  );
}
